// Owned per-element DEAD-CLICK capture → analytics beacon → events_raw (rule 34:
// telemetry rides the SAME edge beacon as every other browser event, NEVER a
// Cloud Run route). A "dead click" is a tap on something that LOOKS interactive
// (a button / role=button / onclick / cursor:pointer element) that produces NO
// effect — no navigation, no DOM change, no scroll, no focus. Clarity/PostHog
// report a page-level dead-click RATE but cannot attribute it to an ELEMENT; this
// owned beacon fingerprints the element + route so the 56.88% site-wide rate
// becomes actionable per conversion element (rule 65's js_error pattern, applied
// to dead clicks). The fingerprint/grouping is finalized in dbt (stg_dead_clicks),
// never baked into the client — grouping must stay re-runnable (rules 23/24/65).
//
// Detection scope is deliberately JS-HANDLER elements (button / role=button /
// onclick / summary / cursor:pointer), NOT plain <a href> — a browser-native link
// navigates on its own and is rarely "dead", whereas a broken React onClick is
// exactly the failure this measures.

import { sendOwnedAnalyticsEvent } from "@/lib/analytics-server";
import { scrubPii } from "@/lib/pii-scrub";

const MAX_DEAD_CLICKS_PER_SESSION = 8;
const PARAM_VALUE_MAX = 256; // mirror of the beacon sink's per-value cap
const LABEL_MAX_CHARS = 64;
const ANCESTOR_WALK = 4;

// Per-document throttle/dedupe (same scope rationale as js-error-beacon): caps the
// flood for THIS page load and de-dupes the same element+route so a rage-tap on
// one dead button can't spend the whole budget on copies.
let sentCount = 0;
const seenKeys = new Set<string>();

/**
 * Test-only: reset the per-document throttle/dedupe state between cases.
 * @internal
 */
export function resetDeadClickBeaconForTests(): void {
  sentCount = 0;
  seenKeys.clear();
}

const INTERACTIVE_TAGS = new Set(["BUTTON", "SUMMARY"]);
const INTERACTIVE_ROLES = new Set(["button", "tab", "menuitem"]);
const INTERACTIVE_INPUT_TYPES = new Set(["submit", "button", "checkbox", "radio"]);

// Styled-clickable (a div/span with cursor:pointer + a handler) — the rule-32
// "looks interactive, isn't" shape. getComputedStyle can throw in detached/SSR
// contexts, so it's isolated here and treated as non-interactive on error.
function hasPointerCursor(el: Element): boolean {
  try {
    return getComputedStyle(el).cursor === "pointer";
  } catch {
    return false;
  }
}

/**
 * Whether an element is one whose tap is expected to run a JS handler (so a
 * no-op tap on it is a genuine dead click). Plain <a href> is EXCLUDED — the
 * browser navigates it natively, so it is rarely dead and would add noise.
 * @internal
 */
export function looksInteractive(el: Element): boolean {
  const tag = el.tagName;
  if (INTERACTIVE_TAGS.has(tag)) return true;
  if (tag === "INPUT" && INTERACTIVE_INPUT_TYPES.has((el as HTMLInputElement).type)) return true;
  if (INTERACTIVE_ROLES.has(el.getAttribute("role") ?? "")) return true;
  if (el.hasAttribute("onclick") || el.hasAttribute("data-dc")) return true;
  return tag !== "A" && hasPointerCursor(el);
}

/**
 * From the clicked node, walk up to {@link ANCESTOR_WALK} ancestors to the nearest
 * element whose tap should run a handler. Returns null for a click on plain text /
 * whitespace / a real link (nothing a dead-click signal should fire for).
 */
export function deadClickTarget(node: EventTarget | null): Element | null {
  let el = node instanceof Element ? node : null;
  for (let i = 0; el && i <= ANCESTOR_WALK; i += 1) {
    if (looksInteractive(el)) return el;
    el = el.parentElement;
  }
  return null;
}

/**
 * The stable, PII-free element label used to GROUP dead clicks. Prefers an
 * explicit `data-dc` id, else a DOM id, else `tag[role]:visible-text` (scrubbed +
 * truncated). The label is the raw grouping seed — dbt normalizes volatile bits
 * (prices, ids) into the final fingerprint, so keep the human-readable text here.
 */
export function deadClickElementLabel(el: Element): string {
  const explicit = el.getAttribute("data-dc") || el.id;
  if (explicit) return explicit.slice(0, PARAM_VALUE_MAX);
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role");
  const text = scrubPii((el.textContent || "").replace(/\s+/g, " ").trim()).slice(
    0,
    LABEL_MAX_CHARS
  );
  const head = role ? `${tag}[${role}]` : tag;
  return (text ? `${head}:${text}` : head).slice(0, PARAM_VALUE_MAX);
}

/** The interaction "role" bucket for the element, for a coarse group-by in dbt. */
export function deadClickRole(el: Element): string {
  const role = el.getAttribute("role");
  if (role) return role;
  const tag = el.tagName.toLowerCase();
  if (tag === "button" || tag === "a" || tag === "summary" || tag === "input") return tag;
  return "other";
}

/**
 * Emit a single `dead_click` beacon, throttled + de-duped per (element|route).
 * NEVER throws. Returns true if a beacon was queued, false if throttled/deduped.
 */
export function reportDeadClick(input: { element: string; role: string; route: string }): boolean {
  try {
    if (typeof window === "undefined") return false;
    if (sentCount >= MAX_DEAD_CLICKS_PER_SESSION) return false;
    const route = scrubPii(input.route || "/").slice(0, PARAM_VALUE_MAX);
    const element = input.element.slice(0, PARAM_VALUE_MAX);
    const key = `${element}|${route}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    sentCount += 1;
    sendOwnedAnalyticsEvent("dead_click", {
      element,
      element_role: input.role.slice(0, PARAM_VALUE_MAX),
      route,
      device_type: window.innerWidth < 768 ? "mobile" : "desktop",
    });
    return true;
  } catch {
    // A telemetry helper must never throw into a click handler.
    return false;
  }
}
